import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Download, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { formatApiErrorDetail } from "./AuthModal";
import { API } from "../lib/constants";

const MODELS = [
  { k: "nano-banana", label: "Nano Banana", sub: "Gemini · fast & fun", bg: "bg-ocean-yellow" },
  { k: "gpt-image-1", label: "GPT Image", sub: "OpenAI · detailed", bg: "bg-ocean-sky" },
];

export default function Generator() {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("nano-banana");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const recent = useQuery({
    queryKey: ["community-memes"],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/memes/community?limit=8`);
      return data;
    },
    refetchInterval: 30000,
  });

  const generate = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post(
        `${API}/memes/generate`,
        { prompt, model },
        { timeout: 120000 }
      );
      return data;
    },
    onSuccess: (data) => {
      setResult(data);
      setError("");
      recent.refetch();
      toast.success("Meme surfaced! It's in the wall below.");
    },
    onError: (e) =>
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!prompt.trim()) {
      setError("Give the whale an idea first");
      return;
    }
    generate.mutate();
  };

  return (
    <div
      data-testid="generator-panel"
      className="sticker-lg relative mt-12 overflow-hidden rounded-3xl bg-ocean-navy p-6 text-white sm:p-10"
    >
      <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
        <div>
          <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.3em] text-ocean-sky">
            <Sparkles className="h-4 w-4" />
            AI Meme Generator
          </p>
          <h3 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">
            Cook your own whale meme.
          </h3>
          <p className="mt-2 text-sm font-medium text-white/70 sm:text-base">
            Type an idea, pick an AI artist, and your meme joins the pod's wall.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="generator-form">
            <textarea
              data-testid="generator-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              maxLength={300}
              rows={3}
              placeholder='e.g. "whale riding a rocket to the moon"'
              className="w-full rounded-2xl border-[3px] border-ocean-sky/50 bg-white/5 px-4 py-3 font-body text-sm font-semibold text-white outline-none placeholder:text-white/40 focus:border-ocean-yellow"
            />
            <div className="grid grid-cols-2 gap-3" data-testid="generator-model-picker">
              {MODELS.map((m) => (
                <button
                  key={m.k}
                  type="button"
                  data-testid={`generator-model-${m.k}`}
                  onClick={() => setModel(m.k)}
                  className={`rounded-2xl border-[3px] border-ocean-navy px-4 py-3 text-left transition-all ${m.bg} text-ocean-navy ${
                    model === m.k
                      ? "shadow-[4px_4px_0_#0F172A] ring-2 ring-white"
                      : "opacity-70 hover:opacity-100"
                  }`}
                >
                  <span className="block font-display text-sm font-semibold">{m.label}</span>
                  <span className="block text-xs font-bold opacity-75">{m.sub}</span>
                </button>
              ))}
            </div>
            {error && (
              <p data-testid="generator-error" className="rounded-xl bg-rose-500/20 px-4 py-2.5 text-sm font-bold text-rose-200">
                {error}
              </p>
            )}
            <button
              data-testid="generator-submit"
              type="submit"
              disabled={generate.isPending}
              className="btn-push sticker flex w-full items-center justify-center gap-2 rounded-2xl bg-ocean-yellow px-6 py-4 font-display text-lg font-semibold text-ocean-navy disabled:opacity-60"
            >
              <Wand2 className="h-5 w-5" />
              {generate.isPending ? "The whale is painting… (~30s)" : "Generate my meme"}
            </button>
          </form>
        </div>

        <div>
          <div
            data-testid="generator-canvas"
            className="flex aspect-square w-full items-center justify-center rounded-3xl border-[3px] border-dashed border-ocean-sky/40 bg-white/5 p-3"
          >
            {generate.isPending ? (
              <div className="flex flex-col items-center gap-3">
                <span className="h-12 w-12 animate-spin rounded-full border-4 border-ocean-sky border-t-transparent" />
                <p className="font-display text-sm font-medium text-white/70">
                  Mixing paints…
                </p>
              </div>
            ) : result ? (
              <motion.img
                data-testid="generator-result"
                src={`${API}${result.image_url}`}
                alt={result.prompt}
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="h-full w-full rounded-2xl border-[3px] border-white/20 object-contain"
              />
            ) : (
              <p className="max-w-[220px] text-center font-display text-base font-medium text-white/50">
                Your freshly generated meme will surface here.
              </p>
            )}
          </div>
          {result && (
            <a
              data-testid="generator-download"
              href={`${API}${result.image_url}`}
              download={`blue-meme-${result.meme_id}.png`}
              className="btn-push sticker-sm mt-4 flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 font-display text-base font-semibold text-ocean-navy"
            >
              <Download className="h-4 w-4" />
              Download meme
            </a>
          )}
        </div>
      </div>

      {(recent.data?.length || 0) > 0 && (
        <div className="mt-8 border-t-[3px] border-white/10 pt-6">
          <p className="text-xs font-extrabold uppercase tracking-[0.3em] text-white/60">
            Fresh from the pod
          </p>
          <div className="mt-4 grid grid-cols-4 gap-3 sm:gap-4" data-testid="generator-recent">
            {recent.data.map((m) => (
              <a
                key={m.meme_id}
                data-testid={`generator-recent-${m.meme_id}`}
                href={`${API}${m.image_url}`}
                download={`blue-meme-${m.meme_id}.png`}
                title={`"${m.prompt}" — tap to download`}
                className="group block"
              >
                <img
                  src={`${API}${m.image_url}`}
                  alt={m.prompt}
                  className="aspect-square w-full rounded-2xl border-[3px] border-ocean-navy bg-white object-cover transition-transform duration-200 group-hover:-translate-y-1"
                  loading="lazy"
                />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
